import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axiosInstance from '../services/axios';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '@chakra-ui/react';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const toast = useToast();

  // 🔹 Fetch all available subscriptions
  const fetchSubscriptions = useCallback(async () => {
    
    setLoading(true);
    try {
      const response = await axiosInstance.get('/stripe/get_all_prices');
    
      setSubscriptions(response.data.data);
    } catch (error) {
      toast({
        title: 'Error fetching subscription plans.',
        description: error.response?.data?.detail || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // 🔹 Fetch the user's current subscription
  const fetchUserSubscription = useCallback(async () => {
    if (!user) return;

    if(user.account_type === "fan"){
      return
    }

    try {
      const response = await axiosInstance.get("/stripe/get_subscription");
      const res = response.data;
      if (res.data === null) {
        setUserSubscription(null);
        // Don't show toast on initial load - only errors
        return;
      }

      setUserSubscription(res.data);
      return;
    } catch (error) {
      setUserSubscription(null);
      // Only show error toast for actual errors, not 404s
      if (error.response?.status !== 404) {
        toast({
          title: error.response?.data?.detail || "Failed to fetch subscription.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [user, toast]);

  // 🔹 Fetch available payment methods
  const getPaymentMethods = useCallback(async () => {

    try {
      const response = await axiosInstance.get('/stripe/get_payment_methods');
      setPaymentMethods(response.data.payment_methods);
    } catch (error) {
      toast({
        title: 'Error fetching payment methods.',
        description: error.response?.data?.detail || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

// (No replacement lines; the commented-out code is removed entirely.)
// inside your SubscriptionContext (or wherever createSubscription lives)

const createSubscription = async ({ paymentMethodId, priceId, coupon }) => {
  if (!user || user.account_type === "fan") return;

  try {
    const payload = {
      price_id: priceId,
      coupon: coupon || "",
      // include paymentMethodId only if non-free; backend will ignore if price is free
      ...(paymentMethodId && { payment_method_id: paymentMethodId }),
    };

    const { data } = await axiosInstance.post("/stripe/create_subscription", payload);

    setUserSubscription(data.subscription);
    toast({
      title: priceId && !paymentMethodId
        ? "Free plan activated!"
        : "Subscription created successfully!",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  } catch (err) {
    toast({
      title: "Error creating subscription",
      description: err.response?.data?.detail || err.message,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
    throw err;
  }
};
  // 🔹 Create a Payment Intent (Saves Payment Method)
  const createPaymentIntent = async (amount, currency, paymentMethodId) => {
    try {
      const response = await axiosInstance.post("/stripe/create_payment_intent", {
        amount,
        currency,
        payment_method_id: paymentMethodId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Payment Intent creation failed.");
    }
  };

  // 🔹 Create a Payment Intent (Without Saving Payment Method)
  const createPaymentIntentWithoutSaving = async (amount, currency, paymentMethodId) => {
    try {
      const response = await axiosInstance.post("/stripe/create_payment_intent_without_saving", {
        amount,
        currency,
        payment_method_id: paymentMethodId,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Payment Intent creation failed.");
    }
  };

  // 🔹 Create Payment Request Intent for Apple Pay/Google Pay
  const createPaymentRequestIntent = async (amount, currency = "usd", label) => {
    try {
      const response = await axiosInstance.post("/stripe/create_payment_request_intent", {
        amount,
        currency,
        label,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Payment Request Intent creation failed.");
    }
  };

  // 🔹 Create a New Payment Method
  const createPaymentMethod = async (cardElement, stripe, billingDetails) => {
    try {
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: billingDetails
      });

      if (error) {
        throw new Error(error.message || 'Failed to create payment method.');
      }
      return paymentMethod.id;
    } catch (error) {
      throw error;
    }
  };

  // 🔹 Add a New Payment Method
  const addPaymentMethod = async (paymentMethodId) => {
    try {
      await axiosInstance.post('/stripe/add_payment_method', { payment_method_id: paymentMethodId });
      await getPaymentMethods();
    } catch (error) {
      throw error;
    }
  };

  // Sets a default payment method by calling the backend endpoint.
  const setDefaultPaymentMethod = async (paymentMethodId) => {
    setLoading(true)
    try {
      
      await axiosInstance.post(
        `/stripe/set_default_payment_method?payment_method_id=${paymentMethodId}`
      );
      // Optionally, refresh or update the local state of payment methods.
      await getPaymentMethods()
    } catch (error) {
      console.error("Error setting default payment method:", error);
      throw error;
    }finally{
      setLoading(false)
    }
  };

  // Deletes a payment method by calling the backend endpoint.
  const deletePaymentMethod = async (paymentMethodId) => {
    setLoading(true)
    try {
      await axiosInstance.delete(
        `/stripe/delete_payment_method?payment_method_id=${paymentMethodId}`
      );
      // Optionally, refresh or update the local state of payment methods.
      await getPaymentMethods()
    } catch (error) {
      console.error("Error deleting payment method:", error);
      throw error;
    }finally{
      setLoading(false)
    }
  };


  // 🔹 Validate a Coupon
  const validateCoupon = async (couponCode, priceId, totalAmount) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/stripe/validate_coupon`, {
        coupon_code: couponCode,
        price_id: priceId,
        transaction_total: totalAmount,
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Validate Buying Coupon
  const validateBuyingCoupon = async (couponCode, totalAmount) => {
    try {
      const response = await axiosInstance.get("/stripe/validate_buying_coupon", {
        params: { coupon_code: couponCode, amount: totalAmount },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const stopAutoRenewal = async () => {
    try {
      await axiosInstance.post('/stripe/stop_auto_renewal');
      await fetchUserSubscription();
    } catch (error) {
      console.error('Error stopping auto-renewal:', error);
      throw error;
    }
  };

  // Start auto-renewal
  const startAutoRenewal = async () => {
    try {
      await axiosInstance.post('/stripe/start_auto_renewal');
      await fetchUserSubscription();
    } catch (error) {
      console.error('Error starting auto-renewal:', error);
      throw error;
    }
  };



const calculateProration = async ({ currentSubscriptionId, newPriceId }) => {
  try {
    const response = await axiosInstance.post("/stripe/calculate_proration", {
      subscription_id: currentSubscriptionId,
      new_price_id: newPriceId,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || "Failed to calculate proration."
    );
  }
};


  const upgradeSubscription = async ({ subscriptionId, newPriceId,coupon }) => {
    if(!user || user.account_type === "fan"){
      return
    }
    try {
      const response = await axiosInstance.post("/stripe/upgrade_subscription", {
        subscription_id: subscriptionId,
        new_price_id: newPriceId,
        coupon: coupon || ""
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Failed to upgrade subscription."
      );
    }
  };

  // 🔹 Fetch Billing Info
  const getBillingInfo = async () => {
    try {
      const response = await axiosInstance.get("/stripe/billing-info");
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // 🔹 Save Billing Info
  const saveBillingInfo = async (billingInfo) => {
    try {
      const response = await axiosInstance.post("/stripe/billing-info", billingInfo);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // 🔹 Cash Out (Payout)
  const cashOut = async (description = "Cash-out to bank") => {
    try {
      const response = await axiosInstance.post("/stripe/cashout", {
        description,
        currency: "usd",
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };



  const createConnectAccount = async () => {
    try {
      const response = await axiosInstance.post("/stripe/connect/create-account");
      return response.data // Adjust if your API response differs
    } catch (error) {
      // Provide a more descriptive error message
      throw new Error(
        error.response?.data?.detail || "Error creating Connect account"
      );
    }
  };

  const generateOnboardingLink = async (returnUrl, refreshUrl) => {
    try {
      const response = await axiosInstance.post(
        "/stripe/connect/generate-onboarding-link",
        {
          return_url: returnUrl,
          refresh_url: refreshUrl,
        }
      );
      return response.data.onboarding_url;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Error generating onboarding link"
      );
    }
  };



  const goToOnboarding = async () => {
    try {
      const onboardingUrl = await generateOnboardingLink(
        `${process.env.REACT_APP_API_URL}/dashboard/billing/info`,
        `${process.env.REACT_APP_API_URL}/dashboard/billing/info`
      );
      // Redirect the user to Stripe's Express onboarding
      window.location.href = onboardingUrl;
    } catch (error) {
      console.error(error);
      alert("Failed to generate onboarding link");
    }
  };

  const createExpressPayout = async (amountInCents, currency = "usd") => {
    try {
      const response = await axiosInstance.post("/stripe/connect/payout", {
        amount_in_cents: amountInCents,
        currency,
      });
      return response.data.payout;
    } catch (error) {
      throw new Error(
        error.response?.data?.detail || "Error creating payout"
      );
    }
  };


  const getConnectAccountDetails = async () => {
    try {
      const response = await axiosInstance.get("/stripe/connect/account");
      return response.data;
    } catch (error) {
      // Handle 404 - endpoint doesn't exist or user doesn't have account
      if (error.response?.status === 404) {
        return null; // Return null instead of throwing error
      }
      throw new Error(
        error.response?.data?.detail || "Failed to retrieve account details."
      );
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const response = await axiosInstance.get("/stripe/connect/payouts");
      return response.data.payouts || [];
    } catch (error) {
      // Handle 400/404 - endpoint issues or no payout history
      if (error.response?.status === 400 || error.response?.status === 404) {
        return []; // Return empty array instead of throwing error
      }
      throw error;
    }
  };



  // 🔹 Check if User is Subscribed
  const isSubscribed = useCallback(() => {
    if(!user || user.account_type === "fan"){
      return false;
    }
    return userSubscription && userSubscription.status === 'active';
  }, [userSubscription, user]);

  // 🔹 Fetch subscriptions & user subscription on mount
  useEffect(() => {
    if (user && user.account_type !== "fan") {
      fetchSubscriptions();
      fetchUserSubscription();
    }
  }, [user]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptions,
        userSubscription,
        loading,
        fetchSubscriptions,
        fetchUserSubscription,
        createSubscription,
        stopAutoRenewal,
        getPaymentMethods,
        addPaymentMethod,
        createPaymentMethod,
        setDefaultPaymentMethod,
        createPaymentIntent,
        validateCoupon,
        validateBuyingCoupon,
        cashOut,
        getBillingInfo,
        saveBillingInfo,
        isSubscribed,
        paymentMethods,
        createPaymentIntentWithoutSaving,
        createPaymentRequestIntent,
        upgradeSubscription,
        createConnectAccount,
        goToOnboarding,
        calculateProration,
        createExpressPayout,
        getConnectAccountDetails,
        fetchPayoutHistory,
        startAutoRenewal,
        deletePaymentMethod

      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use the SubscriptionContext
export const useSubscription = () => useContext(SubscriptionContext);